import { Injectable } from '@angular/core';
import { Bar, BarChartOptions, ChartDataSet, LinearChartData } from 'chart.js';
import { List } from 'immutable';
import { extend, fill, times } from 'lodash';
import { NavigationService } from '../../../navigation/navigation.service';
import { COLORS } from '../../../shared/constants/colors.constant';
import { Dataset } from '../../sub-models/dataset.model';
import { ChartBuilder } from '../chart.builder';

@Injectable()
export class BarChartBuilder extends ChartBuilder {
  private _baseOptions: BarChartOptions = {
    legend: {
      display: false,
    },
    maintainAspectRatio: false,
    scales: {
      xAxes: [],
      yAxes: [
        {
          display: false,
          gridLines: {
            display: false,
          },
        },
      ],
    },
  };

  constructor(protected navigationService: NavigationService) {
    super(navigationService);
  }

  buildBarChart(
    el: any,
    datasets: List<Dataset>,
    labels: string[],
    highlight: number,
    baseline: number,
    options: BarChartOptions,
    trackChartClick: (deeplinkUrl: string) => void
  ): Bar {
    return new Bar(el, {
      data: this.buildChartData(labels, datasets, highlight, baseline),
      options: this.buildChartOptions(
        options,
        datasets,
        baseline,
        trackChartClick
      ),
    });
  }

  buildChartData(
    labels: string[],
    datasets: List<Dataset>,
    highlight: number,
    baseline: number
  ): LinearChartData {
    return {
      labels: labels,
      datasets: this.buildChartDatasets(
        datasets,
        highlight,
        baseline,
        labels.length
      ),
    };
  }

  buildChartDatasets(
    datasets: List<Dataset>,
    highlight: number,
    baseline: number,
    maxLength: number
  ): ChartDataSet[] {
    // not an immutable on purpose in order to fit the chart.js api
    const chartDataSets: ChartDataSet[] = datasets
      .map(
        (dataset: Dataset, key: number): ChartDataSet => {
          return {
            xAxisID: `bar-x-axis-${key}`,
            data: dataset.data.toArray(),
            backgroundColor: this.buildColors(dataset.data.size, highlight),
          };
        }
      )
      .toArray();

    if (baseline > 0) {
      const baselineData = fill(Array(maxLength), baseline);

      // first dataset so its z-index is above the bars
      chartDataSets.unshift({
        xAxisID: 'baseline-x-axis',
        type: 'line',
        data: baselineData,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 2,
        borderColor: COLORS.darkGrey,
      });
    }

    return chartDataSets;
  }

  buildChartOptions(
    customOptions: any,
    datasets: List<Dataset>,
    baseline: number,
    trackChartClick: (deeplinkUrl: string) => void
  ): BarChartOptions {
    const options = extend({}, this._baseOptions, customOptions || {});
    const length = datasets.size;
    const hasBaseline = baseline > 0;

    times(length, (index) => {
      options.scales.xAxes.push(this.buildBarXAxis(index));
    });

    if (hasBaseline) {
      options.scales.xAxes.push(this.buildBaselineXAxis());
    }

    const datasetOffsetUnshift = hasBaseline ? 1 : 0;

    options.tooltips = this.buildTooltips(datasets, datasetOffsetUnshift);
    options.onClick = this.handleClick(
      datasets,
      datasetOffsetUnshift,
      trackChartClick
    );

    return options;
  }

  buildColors(length: number, highlight: number): string[] {
    const colors = fill(Array(length), COLORS.lightBlue);
    colors[highlight] = COLORS.primaryBlue;

    return colors;
  }

  buildBarXAxis(index: number): any {
    return {
      id: `bar-x-axis-${index}`,
      gridLines: {
        display: false,
      },
    };
  }

  buildBaselineXAxis(): any {
    return {
      id: 'baseline-x-axis',
      display: false,
      gridLines: {
        display: false,
      },
    };
  }
}
